<script setup>
import { onMounted, onBeforeMount, watch, watchEffect, computed } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { useRouter, useRoute } from 'vue-router'
import PlanSelectionModal from '@/components/PlanSelectionModal.vue'
import ToastNotification from '@/components/ToastNotification.vue'

const configStore = useConfigStore()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

onBeforeMount(() => {
  // Initialize configurations on load
  configStore.fetchConfigurations().catch(err => {
    console.error("Initial config fetch failed", err)
    alert("Please try again in a few minutes")
  })
})

onMounted(async () => {
  // Note: handleLoginCallback() is called in the router guard (router/index.js)
  // to avoid a race condition where the guard runs before onMounted and
  // redirects to /signin before the token is extracted from the URL hash.

  // Check for 'signout=true' in the URL query parameters
  if (route.query.signout === 'true') {
    await authStore.signout()
  }
})

// Signout fix: immediately clear store data and redirect
watch(() => authStore.isAuthenticated, (authenticated) => {
  if (!authenticated) {
    router.push('/signin')
  }
})

watchEffect(async () => {
  if (authStore.isAuthenticated) {
    await authStore.initialize()
  }
})

// Logic to show Plan Selection Modal
// Show if authenticated, not loading, and NO organization is active
const showPlanSelectionModal = computed(() => {
  if (!authStore.isAuthenticated || authStore.isLoading) return false;

  // If user has no organizations (then skip),
  // If none of them are active, show modal
  // Note: authStore.organizations is an array.
  // We check if length is 0 and skip
  if (!Array.isArray(authStore.organizations) || authStore.organizations.length === 0) return false;

  const hasActiveOrg = authStore.organizations.some(org => org.status === 'active');
  return !hasActiveOrg;
})

</script>

<template>
  <router-view v-if="configStore.configurations" />

  <div v-else class="h-screen w-screen flex items-center justify-center bg-zinc-50">
    <div class="animate-pulse text-gray-400">Loading HasMCP...</div>
  </div>

  <PlanSelectionModal v-if="showPlanSelectionModal" />
  <ToastNotification />
</template>